import { Component, EventEmitter, inject, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NotificationEntity } from '@app/@core/entities';
import { DialogService } from '@ngneat/dialog';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { DateTimeUtility, GetUserId, isMobileView } from '@core/utils';
import { getButtonActions, getText } from '@app/@core/utils/notification-type.utility';
import { ConfirmationDialogType, NotificationActionType, NotificationsActionTypes, NotificationType } from '@app/@core/enums';
import { HumanizePipe } from '@shared/pipes';
import { ConfirmationDialogComponent, SingleInputCreateComponent } from '@app/platforms/web/_shared/dialogs';
import { Observable, Subscription } from 'rxjs';
import { NotificationsComposition } from '@shared/compositions';
import { FetchProfileImageDirective } from '@shared/directives';

@Component({
  selector: 'app-notifications',
  templateUrl: './notifications.component.html',
  styleUrls: ['./notifications.component.scss'],
  imports: [TranslateModule, NgxSkeletonLoaderModule, CommonModule, HumanizePipe, FetchProfileImageDirective],
})
export class NotificationsComponent implements OnInit, OnDestroy {
  isLoading = true;
  notifications$: Observable<NotificationEntity[]>;
  notifications: NotificationEntity[];
  @Output() closePopover = new EventEmitter();
  getButtonActions = getButtonActions;
  limit = 50;
  protected readonly ActionType = NotificationActionType;
  protected readonly Infinity = Infinity;
  private readonly _dialogService = inject(DialogService);
  private readonly _translate = inject(TranslateService);
  private readonly _notificationsComposition = inject(NotificationsComposition);
  private _notificationsSubscription: Subscription;

  ngOnInit() {
    this._subscribeToNotifications();
    this._notificationsComposition.closePopover$.subscribe({
      next: (close) => {
        if (close) {
          this.closePopover.emit();
        }
      },
    });
  }

  getMemberId(notification: NotificationEntity): number {
    if (notification?.firstName && notification?.lastName) {
      return +this._notificationsComposition?.teamUsers?.find((user) => user.firstName === notification.firstName && user.lastName === notification.lastName)?.memberId;
    }
    return null;
  }

  getTimeAgo(dateTime: string): string {
    return DateTimeUtility.getTimeAgo(dateTime, this._translate);
  }

  getNotificationText(notificationType: NotificationsActionTypes): string {
    return getText(notificationType, this._translate);
  }

  showMore(): void {
    if (this.limit + 20 < this.notifications.length) {
      this.limit += 20;
    } else {
      this.limit = Infinity;
    }
  }

  /**
   * Main click handler – delegates to accept, deny or view.
   */
  handleButtonClick(notification: NotificationEntity, action: NotificationActionType, notificationSection: NotificationType) {
    switch (action) {
      case NotificationActionType.Accept:
        this._handleAccept(notification);
        break;
      case NotificationActionType.Deny:
        this._handleDeny(notification, notificationSection);
        break;
      case NotificationActionType.View:
        this._handleView(notification);
        break;
      default:
        break;
    }
  }

  ngOnDestroy(): void {
    if (this._notificationsSubscription) {
      this._notificationsSubscription.unsubscribe();
    }
  }

  openNotificationSectionDetails(notification: NotificationEntity) {
    const viewAction = this._getViewAction(notification);
    if (viewAction === this._defaultViewAction) {
      const { path, params } = this._getSectionDetailsPath(notification);
      this._notificationsComposition.reloadCurrentRoute(path, params);
    } else {
      viewAction();
    }
  }

  // ---------------------------------------------------------------------------------------------------------------------
  // ------------------------------------------ Private Protected Functions ----------------------------------------------
  // ---------------------------------------------------------------------------------------------------------------------

  private _handleView(notification: NotificationEntity): void {
    const viewFn = this._getViewAction(notification);
    viewFn();
  }

  private _handleAccept(notification: NotificationEntity): void {
    let confirmationType: ConfirmationDialogType = ConfirmationDialogType.Accept;

    switch (notification.notificationType) {
      case NotificationsActionTypes.GroupNotifications:
        confirmationType = ConfirmationDialogType.APPROVE_JOIN;
        break;
      default:
        confirmationType = ConfirmationDialogType.Accept;
        break;
    }

    this._dialogService
      .open(ConfirmationDialogComponent, {
        closeButton: false,
        data: {
          category: notification.section,
          type: confirmationType,
        },
      })
      .afterClosed$.subscribe({
        next: (confirmed) => {
          if (confirmed) {
            const actionFn = this._getAcceptAction(notification);
            actionFn();
          }
        },
      });
  }

  private _handleDeny(notification: NotificationEntity, notificationSection: NotificationType): void {
    const appendCategory = notificationSection ? ' ' + notificationSection.charAt(0).toUpperCase() + notificationSection.slice(1) : '';

    // Determine if this type requires an input dialog (to collect a deny reason) or just a simple confirmation.
    if (this._requiresDenyInput(notification.notificationType)) {
      // For types that require a reason input (such as unapproved course, unapproved event, admin unapproved tasks, etc.)
      this._dialogService
        .open(SingleInputCreateComponent, {
          closeButton: false,
          data: {
            title: this._translate.instant('Are you sure you want to unapprove this' + appendCategory),
            label: this._translate.instant('Message'),
            inputType: 'textArea',
          },
        })
        .afterClosed$.subscribe({
          next: (reason) => {
            if (reason) {
              const denyFn = this._getDenyAction(notification, reason);
              denyFn();
            }
          },
        });
    } else {
      // For types that only require a simple confirmation (e.g. deny invited events, deny invited courses, etc.)
      this._dialogService
        .open(ConfirmationDialogComponent, {
          closeButton: false,
          data: { category: notificationSection, type: ConfirmationDialogType.Deny },
        })
        .afterClosed$.subscribe({
          next: (confirmed) => {
            if (confirmed) {
              const denyFn = this._getDenyAction(notification, ''); // pass an empty reason
              denyFn();
            }
          },
        });
    }
  }

  //----------------------------------------------------------------------------------------------------------
  // --------- Mapping helper methods – return functions that call the appropriate composition method --------
  //----------------------------------------------------------------------------------------------------------

  /**
   * Helper that returns true for those notification types that (per legacy)
   * require a reason input when denying.
   */
  private _requiresDenyInput(type: NotificationsActionTypes): boolean {
    const typesRequiringInput: NotificationsActionTypes[] = [
      NotificationsActionTypes.GroupForApprovals,
      NotificationsActionTypes.UpdateGroupNotifications,
      NotificationsActionTypes.NewsNotifications,
      NotificationsActionTypes.UpdateNewsNotifications,
      NotificationsActionTypes.EventsNotifications,
      NotificationsActionTypes.EventUpdateNotifications,
      NotificationsActionTypes.CoursesNotifications,
      NotificationsActionTypes.UpdateCourseNotifications,
      NotificationsActionTypes.TaskNotifications,
      NotificationsActionTypes.UpdateTasksNotifications,
      NotificationsActionTypes.SurveyNotifications,
      NotificationsActionTypes.UpdateSurveyNotification,
      NotificationsActionTypes.RoomNotifications,
      NotificationsActionTypes.RoomUpdateNotifications,
      NotificationsActionTypes.InstructorNotifications,
      NotificationsActionTypes.UpdateInstructorNotifications,
      NotificationsActionTypes.FaqsNotifications,
      NotificationsActionTypes.UpdateFaqsNotificationToadmin,
    ];
    return typesRequiringInput.includes(type);
  }

  private _getViewAction(notification: NotificationEntity): () => void {
    switch (notification.notificationType) {
      // GROUPS

      case NotificationsActionTypes.UpdatedGroupNotificationForExistingUsers:
        return () => this._notificationsComposition._viewUpdatedGroup(notification.id);
      case NotificationsActionTypes.DenyGroupNotifications:
        return () => this._notificationsComposition._viewGroup(notification.id);
      case NotificationsActionTypes.AcceptGroupUserNotifications:
        return () => this._notificationsComposition._viewAcceptedGroup(notification.id);
      case NotificationsActionTypes.GetPublishedGroupNotificationToCreator:
        return () => this._notificationsComposition._readPublishedGroup(notification.id);

      // NEWS

      case NotificationsActionTypes.DenyNewsNotifications:
        return () => this._notificationsComposition._viewNews(notification.id);
      case NotificationsActionTypes.ApprovedUpdateNewsNotificationToCreator:
        return () => this._notificationsComposition._viewApproveUpdateNewsByAdmin(notification.id);
      case NotificationsActionTypes.ApprovedPublishNewsNotificationToCreator:
        return () => this._notificationsComposition._viewApprovePublishNewsByAdmin(notification.id);

      // EVENTS

      case NotificationsActionTypes.DenyEventNotificationsToCreator:
        return () => this._notificationsComposition._viewDenyPublishEventByAdmin(notification.id);
      case NotificationsActionTypes.ApprovedEventNotificationsToCreator:
        return () => this._notificationsComposition._viewAcceptPublishEventByAdmin(notification.id);
      case NotificationsActionTypes.ApprovedUpdateEventNotificationsToCreator:
        return () => this._notificationsComposition._viewApproveUpdateEventByAdmin(notification.id);
      case NotificationsActionTypes.UpdatedEventNotificationForExistingUsers:
        return () => this._notificationsComposition._viewUpdateEvent(notification.id);
      case NotificationsActionTypes.InvitedEventsNotifications: // this invited event was added here for user to have multiple option to accept events
        return () => this._notificationsComposition._viewUpdateEvent(notification.id);

      // COURSES

      case NotificationsActionTypes.UserJoinedByCreatorNotification:
        return () => this._notificationsComposition._viewJoinedCourse(notification.id);
      case NotificationsActionTypes.UpdatedCourseNotificationForExistingUsers:
        return () => this._notificationsComposition._viewUpdateCourse(notification.id);
      case NotificationsActionTypes.InternalCourseInstructorNotifications:
        return () => this._notificationsComposition._viewInternalCourse(notification.id);
      case NotificationsActionTypes.InternalUpdateCourseInstructorNotifications:
        return () => this._notificationsComposition._viewInternalUpdateCourse(notification.id);
      case NotificationsActionTypes.ApprovedCourseNotificationsToCreator:
        return () => this._notificationsComposition._viewApprovePublishCourseByAdmin(notification.id);
      case NotificationsActionTypes.ApprovedUpdateCourseNotificationsToCreator:
        return () => this._notificationsComposition._viewApproveUpdateCourseByAdmin(notification.id);
      case NotificationsActionTypes.DenyCourseNotificationsToCreator:
        return () => this._notificationsComposition._viewDenyPublishCourseByAdmin(notification.id);

      // TASKS

      case NotificationsActionTypes.GetDenyPublishTaskByAdminNotificationstoCreator:
        return () => this._notificationsComposition._viewDenyPublishTaskByAdmin(notification.id);
      case NotificationsActionTypes.GetAcceptPublishTaskByAdminNotificationstoCreator:
        return () => this._notificationsComposition._viewAcceptPublishTaskByAdmin(notification.id);
      case NotificationsActionTypes.AcceptUpdatedTaskByAdminNotificationstoCreator:
        return () => this._notificationsComposition._viewAcceptUpdatedTaskByAdmin(notification.id);
      case NotificationsActionTypes.UpdatedTaskNotificationForExistingUsers:
        return () => this._notificationsComposition._viewUpdatedTask(notification.id);
      case NotificationsActionTypes.CompletedTaskNotificationToOrganizer:
        return () => this._notificationsComposition._viewCompletedTask(notification.id);
      case NotificationsActionTypes.UnapprovedTasks:
        return () => this._notificationsComposition._acceptInvitedTask(notification.id);
      case NotificationsActionTypes.UnapprovedSubTask:
        return () => this._notificationsComposition._acceptInvitedSubTask(notification.id);

      // SURVEYS

      case NotificationsActionTypes.GetAcceptPublishSurveyByAdminNotificationstoCreator:
        return () => this._notificationsComposition._viewAcceptPublishSurveyByAdmin(notification.id);
      case NotificationsActionTypes.PublishUpdateSurveyByAdminNotificationstoCreator:
        return () => this._notificationsComposition._viewPublishUpdateSurveyByAdmin(notification.id);
      case NotificationsActionTypes.GetDenyPublishSurveyByAdminNotificationstoCreator:
        return () => this._notificationsComposition._viewDenyPublishSurveyByAdmin(notification.id);
      case NotificationsActionTypes.InvitedSurveyNotifications:
        return () => this._notificationsComposition._acceptInvitedSurvey(notification.id);

      // ROOMS

      case NotificationsActionTypes.ApprovedRoomNotificationsToCreator:
        return () => this._notificationsComposition._viewApprovePublishRoomByAdmin(notification.id);
      case NotificationsActionTypes.ApprovedUpdateRoomNotificationsToCreator:
        return () => this._notificationsComposition._viewApproveUpdateRoomByAdmin(notification.id);
      case NotificationsActionTypes.DenyRoomNotificationsToCreator:
        return () => this._notificationsComposition._viewDenyPublishRoomByAdmin(notification.id);

      // INSTRUCTORS

      case NotificationsActionTypes.DenyInstructNotificationsToCreator:
        return () => this._notificationsComposition._viewDenyPublishInstructorByAdmin(notification.id);
      case NotificationsActionTypes.ApprovedInstructNotificationsToCreator:
        return () => this._notificationsComposition._viewApprovePublishInstructorByAdmin(notification.id);
      case NotificationsActionTypes.ApprovedUpdateInstructNotificationsToCreator:
        return () => this._notificationsComposition._viewApproveUpdateInstructorByAdmin(notification.id);

      // FAQS

      case NotificationsActionTypes.ApprovedFaqNotification: // it is also in accept action
        return () => this._notificationsComposition._readApprovedFaqs(notification.id);
      case NotificationsActionTypes.UpdateFaqsNotificationToAuthor: // it is also in accept action
        return () => this._notificationsComposition._readUpdatedFaqs(notification.id);
      case NotificationsActionTypes.FaqsDenyNotificationToAuthor:
        return () => this._notificationsComposition._viewFaqs(notification.id);
      default:
        return this._defaultViewAction;
    }
  }

  private readonly _defaultViewAction = () => {
    console.error('No view action defined for notification type.');
  };

  private _getAcceptAction(notification: NotificationEntity): () => void {
    switch (notification.notificationType) {
      // GROUPS

      case NotificationsActionTypes.GroupNotifications:
        return () => this._notificationsComposition._joinGroup(notification.id, +notification?.userId);
      case NotificationsActionTypes.UpdateGroupNotifications:
        return () => this._notificationsComposition._acceptUpdatedGroup(notification.id);
      case NotificationsActionTypes.UnapprovedUserGroups:
        return () => this._notificationsComposition._acceptGroup(notification.id);
      case NotificationsActionTypes.GroupForApprovals:
        return () => this._notificationsComposition._approvedGroup(notification.id);
      case NotificationsActionTypes.AcceptGroupUserNotifications:
        return () => this._notificationsComposition._viewAcceptedGroup(notification.id);
      case NotificationsActionTypes.GetPublishedGroupNotificationToCreator:
        return () => this._notificationsComposition._readPublishedGroup(notification.id);

      // NEWS

      case NotificationsActionTypes.NewsNotifications:
        return () => this._notificationsComposition._acceptNews(notification.id);
      case NotificationsActionTypes.UpdateNewsNotifications:
        return () => this._notificationsComposition._acceptUpdateNews(notification.id);

      // EVENTS

      case NotificationsActionTypes.EventsNotifications:
        return () => this._notificationsComposition._approvedEvents(notification.id);
      case NotificationsActionTypes.EventUpdateNotifications:
        return () => this._notificationsComposition._approvedUpdateEvents(notification.id);
      //   TODO: we need to update this action to accept invited events and navigate user to the event details page
      // case NotificationsActionTypes.InvitedEventsNotifications:
      // return () => this._notificationsComposition._acceptInvitedEvent(notification.id);

      // COURSES

      case NotificationsActionTypes.CoursesNotifications:
        return () => this._notificationsComposition._approvedCourses(notification.id);
      case NotificationsActionTypes.UpdateCourseNotifications:
        return () => this._notificationsComposition._approvedUpdateCourses(notification.id);
      case NotificationsActionTypes.InvitedCoursesNotifications:
        return () => this._notificationsComposition._acceptInvitedCourse(notification.id);
      case NotificationsActionTypes.UserWantsToJoinCourseNotificationToCreator:
        return () => this._notificationsComposition._acceptJoinUser(notification.id, notification.userId);
      case NotificationsActionTypes.ApprovedCourseNotificationsToCreator:
        return () => this._notificationsComposition._approvedCourses(notification.id);
      case NotificationsActionTypes.ApprovedUpdateCourseNotificationsToCreator:
        return () => this._notificationsComposition._approvedUpdateCourses(notification.id);

      // TASKS

      case NotificationsActionTypes.TaskNotifications:
        return () => this._notificationsComposition._adminApprovedTasks(notification.id);
      case NotificationsActionTypes.UpdateTasksNotifications:
        return () => this._notificationsComposition._adminApprovedUpdateTasks(notification.id);
      case NotificationsActionTypes.UnapprovedTasks:
        return () => this._notificationsComposition._acceptInvitedTask(notification.id);
      case NotificationsActionTypes.UnapprovedSubTask:
        return () => this._notificationsComposition._acceptInvitedSubTask(notification.id);

      // SURVEYS

      case NotificationsActionTypes.SurveyNotifications:
        return () => this._notificationsComposition._approvedSurvey(notification.id);
      case NotificationsActionTypes.UpdateSurveyNotification:
        return () => this._notificationsComposition._approvedUpdatedSurvey(notification.id);
      case NotificationsActionTypes.InvitedSurveyNotifications:
        return () => this._notificationsComposition._acceptInvitedSurvey(notification.id);
      case NotificationsActionTypes.GetAcceptPublishSurveyByAdminNotificationstoCreator:
        return () => this._notificationsComposition._viewAcceptPublishSurveyByAdmin(notification.id);
      case NotificationsActionTypes.PublishUpdateSurveyByAdminNotificationstoCreator:
        return () => this._notificationsComposition._viewPublishUpdateSurveyByAdmin(notification.id);

      // ROOMS

      case NotificationsActionTypes.RoomNotifications:
        return () => this._notificationsComposition._approvedRooms(notification.id);
      case NotificationsActionTypes.RoomUpdateNotifications:
        return () => this._notificationsComposition._approvedUpdateRooms(notification.id);

      // INSTRUCTORS

      case NotificationsActionTypes.InstructorNotifications:
        return () => this._notificationsComposition._approvedInstructors(notification.id);
      case NotificationsActionTypes.UpdateInstructorNotifications:
        return () => this._notificationsComposition._approvedUpdateInstructors(notification.id);

      // FAQS

      case NotificationsActionTypes.FaqsNotifications:
        return () => this._notificationsComposition._approvedFaqs(notification.id);
      case NotificationsActionTypes.UpdateFaqsNotificationToadmin:
        return () => this._notificationsComposition._approveUpdateFaqs(notification.id);
      case NotificationsActionTypes.ApprovedFaqNotification: // it is also in view action
        return () => this._notificationsComposition._readApprovedFaqs(notification.id);
      case NotificationsActionTypes.UpdateFaqsNotificationToAuthor: // it is also in view action
        return () => this._notificationsComposition._readUpdatedFaqs(notification.id);

      case NotificationsActionTypes.FaqsCategoryNotifications:
        return () => this._notificationsComposition._approvedFaqsCategory(notification.id);

      // MESSAGE

      case NotificationsActionTypes.MessageNotifications:
        return () => this._notificationsComposition._acceptMessage(notification.id);
      default:
        return () => {
          console.warn('No accept action defined for notification type:', notification.notificationType);
        };
    }
  }

  private _getDenyAction(notification: NotificationEntity, reason: string): () => void {
    switch (notification.notificationType) {
      // GROUPS

      case NotificationsActionTypes.GroupNotifications:
        return () => this._notificationsComposition._denyGroup(notification.id, notification?.userId);
      case NotificationsActionTypes.UpdateGroupNotifications:
        return () => this._notificationsComposition._unapproveDenyGroup(notification.id, reason);
      case NotificationsActionTypes.UnapprovedUserGroups:
        return () => this._notificationsComposition._denyGroup(notification.id, +GetUserId());
      case NotificationsActionTypes.GroupForApprovals:
        return () => this._notificationsComposition._unapproveDenyGroup(notification.id, reason);

      // NEWS

      case NotificationsActionTypes.NewsNotifications:
        return () => this._notificationsComposition._denyNews(notification.id, reason);
      case NotificationsActionTypes.UpdateNewsNotifications:
        return () => this._notificationsComposition._denyNews(notification.id, reason);

      // EVENTS

      case NotificationsActionTypes.EventsNotifications:
        return () => this._notificationsComposition._unapprovedEvent(notification.id, reason);
      case NotificationsActionTypes.EventUpdateNotifications:
        return () => this._notificationsComposition._unapprovedEvent(notification.id, reason);
      case NotificationsActionTypes.InvitedEventsNotifications:
        return () => this._notificationsComposition._denyInvitedEvent(notification.id);

      // COURSES

      case NotificationsActionTypes.CoursesNotifications:
        return () => this._notificationsComposition._unapprovedCourse(notification.id, reason);
      case NotificationsActionTypes.UpdateCourseNotifications:
        return () => this._notificationsComposition._unapprovedCourse(notification.id, reason);
      case NotificationsActionTypes.UserWantsToJoinCourseNotificationToCreator:
        return () => this._notificationsComposition._denyJoinUser(notification.id);
      case NotificationsActionTypes.InvitedCoursesNotifications:
        return () => this._notificationsComposition._denyInvitedCourse(notification.id);

      // TASKS

      case NotificationsActionTypes.TaskNotifications:
        return () => this._notificationsComposition._adminUnapprovedTasks(notification.id, reason);
      case NotificationsActionTypes.UpdateTasksNotifications:
        return () => this._notificationsComposition._adminUnapprovedTasks(notification.id, reason);

      // SURVEYS

      case NotificationsActionTypes.SurveyNotifications:
        return () => this._notificationsComposition._unapprovedSurvey(notification.id, reason);
      case NotificationsActionTypes.UpdateSurveyNotification:
        return () => this._notificationsComposition._unapprovedSurvey(notification.id, reason);

      // ROOMS

      case NotificationsActionTypes.RoomNotifications:
        return () => this._notificationsComposition._unapprovedRooms(notification.id, reason);
      case NotificationsActionTypes.RoomUpdateNotifications:
        return () => this._notificationsComposition._unapprovedRooms(notification.id, reason);

      // INSTRUCTORS

      case NotificationsActionTypes.InstructorNotifications:
        return () => this._notificationsComposition._unapprovedInstuctors(notification.id, reason);
      case NotificationsActionTypes.UpdateInstructorNotifications:
        return () => this._notificationsComposition._unapprovedInstuctors(notification.id, reason);

      // FAQS

      case NotificationsActionTypes.FaqsNotifications:
        return () => this._notificationsComposition._denyFaqs(notification.id, reason);
      case NotificationsActionTypes.UpdateFaqsNotificationToadmin:
        return () => this._notificationsComposition._denyFaqs(notification.id, reason);

      case NotificationsActionTypes.FaqsCategoryNotifications:
        return () => this._notificationsComposition._unapprovedFaqsCategory(notification.id);

      // MESSAGE

      case NotificationsActionTypes.MessageNotifications:
        return () => this._notificationsComposition._denyMessage(notification.id);
      default:
        return () => {
          console.warn('No deny action defined for notification type:', notification.notificationType);
        };
    }
  }

  private _getSectionDetailsPath(notification: NotificationEntity): { path: string; params?: Record<string, any> } {
    const { id, notificationType } = notification;
    const mobile = isMobileView();

    // GROUPS: path includes the id in the URL
    if (
      [
        NotificationsActionTypes.GroupNotifications,
        NotificationsActionTypes.UpdateGroupNotifications,
        NotificationsActionTypes.UpdatedGroupNotificationForExistingUsers,
        NotificationsActionTypes.UnapprovedUserGroups,
        NotificationsActionTypes.GroupForApprovals,
        NotificationsActionTypes.DenyGroupNotifications,
        NotificationsActionTypes.AcceptGroupUserNotifications,
        NotificationsActionTypes.GetPublishedGroupNotificationToCreator,
        NotificationsActionTypes.GroupNotificationExistingUsers,
      ].includes(notificationType)
    ) {
      return { path: mobile ? `/m/groups/${id}/details` : `/groups/${id}/details` };
    }

    // NEWS: path doesn’t embed the id so we add it as a query parameter
    if (
      [
        NotificationsActionTypes.NewsNotifications,
        NotificationsActionTypes.UpdateNewsNotifications,
        NotificationsActionTypes.DenyNewsNotifications,
        NotificationsActionTypes.ApprovedPublishNewsNotificationToCreator,
        NotificationsActionTypes.ApprovedUpdateNewsNotificationToCreator,
      ].includes(notificationType)
    ) {
      return { path: mobile ? '/m/club-wall/news' : '/discover/news', params: { newsId: id } };
    }

    // EVENTS: mobile embeds id in the path; desktop gets it as a query parameter
    if (
      [
        NotificationsActionTypes.EventsNotifications,
        NotificationsActionTypes.EventUpdateNotifications,
        NotificationsActionTypes.DenyEventNotificationsToCreator,
        NotificationsActionTypes.ApprovedEventNotificationsToCreator,
        NotificationsActionTypes.ApprovedUpdateEventNotificationsToCreator,
        NotificationsActionTypes.InvitedEventsNotifications,
        NotificationsActionTypes.UpdatedEventNotificationForExistingUsers,
      ].includes(notificationType)
    ) {
      if (mobile) {
        return { path: `/m/club-wall/events/${id}/details`, params: { source: 'events' } };
      }
      return { path: '/events', params: { eventId: id } };
    }

    // TASKS: both mobile and desktop require a query parameter for the id
    if (
      [
        NotificationsActionTypes.TaskNotifications,
        NotificationsActionTypes.UpdateTasksNotifications,
        NotificationsActionTypes.UnapprovedTasks,
        NotificationsActionTypes.UnapprovedSubTask,
        NotificationsActionTypes.GetDenyPublishTaskByAdminNotificationstoCreator,
        NotificationsActionTypes.GetAcceptPublishTaskByAdminNotificationstoCreator,
        NotificationsActionTypes.AcceptUpdatedTaskByAdminNotificationstoCreator,
        NotificationsActionTypes.UpdatedTaskNotificationForExistingUsers,
        NotificationsActionTypes.CompletedTaskNotificationToOrganizer,
      ].includes(notificationType)
    ) {
      return { path: mobile ? '/m/tasks' : '/tasks', params: { taskId: id } };
    }

    // ROOMS: add id as query parameter
    if (
      [
        NotificationsActionTypes.RoomNotifications,
        NotificationsActionTypes.RoomUpdateNotifications,
        NotificationsActionTypes.ApprovedRoomNotificationsToCreator,
        NotificationsActionTypes.ApprovedUpdateRoomNotificationsToCreator,
        NotificationsActionTypes.DenyRoomNotificationsToCreator,
      ].includes(notificationType)
    ) {
      return { path: mobile ? '/m/courses/rooms' : '/courses/rooms', params: { roomId: id } };
    }

    // INSTRUCTORS: add id as query parameter
    if (
      [
        NotificationsActionTypes.InstructorNotifications,
        NotificationsActionTypes.UpdateInstructorNotifications,
        NotificationsActionTypes.DenyInstructNotificationsToCreator,
        NotificationsActionTypes.ApprovedInstructNotificationsToCreator,
        NotificationsActionTypes.ApprovedUpdateInstructNotificationsToCreator,
      ].includes(notificationType)
    ) {
      return { path: mobile ? '/m/courses/instructors' : '/courses/instructors', params: { instructorId: id } };
    }

    // FAQS: add id as query parameter
    if (
      [
        NotificationsActionTypes.FaqsNotifications,
        NotificationsActionTypes.UpdateFaqsNotificationToadmin,
        NotificationsActionTypes.ApprovedFaqNotification,
        NotificationsActionTypes.UpdateFaqsNotificationToAuthor,
        NotificationsActionTypes.FaqsDenyNotificationToAuthor,
        NotificationsActionTypes.FaqsCategoryNotifications,
      ].includes(notificationType)
    ) {
      return { path: mobile ? '/m/club-tools/faq' : '/club-tools/faq', params: { faqId: id } };
    }

    // SURVEYS: add id as query parameter
    if (
      [
        NotificationsActionTypes.SurveyNotifications,
        NotificationsActionTypes.UpdateSurveyNotification,
        NotificationsActionTypes.InvitedSurveyNotifications,
        NotificationsActionTypes.GetAcceptPublishSurveyByAdminNotificationstoCreator,
        NotificationsActionTypes.PublishUpdateSurveyByAdminNotificationstoCreator,
        NotificationsActionTypes.GetDenyPublishSurveyByAdminNotificationstoCreator,
      ].includes(notificationType)
    ) {
      return { path: mobile ? '/m/club-tools/surveys' : '/club-tools/surveys', params: { surveyId: id } };
    }

    // COURSES: mobile embeds the id, desktop uses a query parameter
    if (
      [
        NotificationsActionTypes.CoursesNotifications,
        NotificationsActionTypes.UpdateCourseNotifications,
        NotificationsActionTypes.UserWantsToJoinCourseNotificationToCreator,
        NotificationsActionTypes.UserJoinedByCreatorNotification,
        NotificationsActionTypes.InvitedCoursesNotifications,
        NotificationsActionTypes.UpdatedCourseNotificationForExistingUsers,
        NotificationsActionTypes.InternalCourseInstructorNotifications,
        NotificationsActionTypes.InternalUpdateCourseInstructorNotifications,
        NotificationsActionTypes.ApprovedCourseNotificationsToCreator,
        NotificationsActionTypes.ApprovedUpdateCourseNotificationsToCreator,
        NotificationsActionTypes.DenyCourseNotificationsToCreator,
      ].includes(notificationType)
    ) {
      return mobile ? { path: `/m/courses/${id}/details` } : { path: '/courses/', params: { courseId: id } };
    }

    // Fallback route
    return { path: '/' };
  }

  private _subscribeToNotifications(): void {
    this.isLoading = true;
    this.notifications$ = this._notificationsComposition.notifications$;
    this._notificationsSubscription = this.notifications$.subscribe({
      next: (resp) => {
        this.notifications = resp.sort((a, b) => {
          const dateA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
          const dateB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;

          if (isNaN(dateA)) return 1;
          if (isNaN(dateB)) return -1;

          return dateB - dateA;
        });
        this.isLoading = false;
      },
    });
  }
}
